import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import * as logger from '../utils/logger';

const apiUrl = import.meta.env.VITE_BACKEND_URL;

export default function SEOOverviewAuditDetail({
  storeId,
  validationDisabled = false,
  quotaExceeded = null,
  refreshTrigger = 0,
  onQuickAction,
  onFixErrors,
}) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!storeId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${apiUrl}/api/seo-cache/overview-stats?id=${storeId}&_t=${Date.now()}`
        );
        if (!res.ok) throw new Error('Failed to load overview stats');
        const data = await res.json();
        if (!cancelled) setStats(data);
      } catch (e) {
        logger.error('[SEOOverviewAuditDetail] load failed', e);
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [storeId, refreshTrigger]);

  const quotaFromStats = stats?.cacheMetadata?.quota_usage;
  const isQuotaExceededFromStats = useMemo(() => {
    if (!quotaFromStats) return false;
    return !quotaFromStats.unlimited && (quotaFromStats.remaining || 0) <= 0;
  }, [quotaFromStats]);

  const isQuotaExceededOverall = !!quotaExceeded || isQuotaExceededFromStats;

  const totalErrors = stats?.totalErrors ?? 0;
  const metaErrors = stats?.metaErrors ?? 0;
  const contentErrors = stats?.contentErrors ?? 0;
  const imageErrors = stats?.imageErrors ?? 0;
  const brokenErrors = stats?.brokenErrors ?? 0;
  const pagesAudited = stats?.pagesAudited ?? { current: 0, total: 0 };

  const isPartialAudit =
    !loading &&
    pagesAudited.total > 0 &&
    pagesAudited.current < pagesAudited.total;

  const handleFixErrors = () => {
    if (onFixErrors) {
      onFixErrors();
    } else {
      window.location.hash = 'seo-optimizer';
    }
  };

  return (
    <div
      className="card border-0 shadow-lg"
      style={{
        borderRadius: '12px',
        border: totalErrors > 0 ? '2px solid #dc2626' : '2px solid #22c55e',
        boxShadow:
          totalErrors > 0
            ? '0 4px 20px rgba(220, 38, 38, 0.1)'
            : '0 4px 20px rgba(34, 197, 94, 0.1)',
      }}
    >
      <div className="card-body p-4">
        {isPartialAudit && totalErrors > 0 && (
          <div
            role="status"
            className="mb-3"
            style={{
              padding: '14px 16px',
              borderRadius: '10px',
              background: 'linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%)',
              border: '2px solid #f59e0b',
            }}
          >
            <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#78350f', marginBottom: '6px' }}>
              {t('dashboard.errorsSampleOnlyTitle')}
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#92400e', lineHeight: 1.45 }}>
              {t('dashboard.errorsSampleOnlyBody', {
                count: totalErrors,
                current: pagesAudited.current,
                total: pagesAudited.total,
              })}
            </div>
          </div>
        )}

        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <div
              style={{
                fontSize: '0.85rem',
                fontWeight: 700,
                color: totalErrors > 0 ? '#dc2626' : '#22c55e',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '0.5rem',
              }}
            >
              {totalErrors > 0
                ? `⚠️ ${t('dashboard.issuesDetected')}`
                : `✓ ${t('dashboard.allClear')}`}
            </div>
            {totalErrors > 0 ? (
              <div className="mb-2">
                <span
                  className="dashboard-number-cell"
                  style={{
                    fontSize: '2.5rem',
                    fontWeight: 900,
                    color: '#dc2626',
                    lineHeight: 1,
                    fontVariantNumeric: 'tabular-nums',
                    minWidth: '2.5ch',
                    display: 'inline-block',
                  }}
                >
                  {loading ? '…' : totalErrors}
                </span>
                <span style={{ fontSize: '1.1rem', color: '#6b7280', marginLeft: '0.5rem' }}>
                  {isPartialAudit
                    ? t('dashboard.seoErrorsInAuditedPages')
                    : t('dashboard.seoErrorsFound')}
                </span>
              </div>
            ) : (
              <p style={{ fontSize: '0.95rem', color: '#6b7280', marginBottom: 0 }}>
                {t('dashboard.noSeoErrors')}
              </p>
            )}
          </div>
          {totalErrors > 0 && (
            <button
              type="button"
              className="btn btn-lg fw-bold"
              style={{
                fontSize: '1rem',
                background: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                whiteSpace: 'nowrap',
              }}
              onClick={handleFixErrors}
              disabled={loading}
            >
              {t('dashboard.fixTheseErrors')} <ArrowRight size={18} className="ms-2" />
            </button>
          )}
        </div>

        {totalErrors > 0 && (
          <div className="row g-2">
            {[
              { key: 'meta', value: metaErrors, color: '#dc2626', label: t('dashboard.meta') },
              { key: 'content', value: contentErrors, color: '#f59e0b', label: t('dashboard.content') },
              { key: 'images', value: imageErrors, color: 'var(--app-primary)', label: t('dashboard.images') },
              { key: 'broken', value: brokenErrors, color: '#ef4444', label: t('dashboard.broken') },
            ].map((item) => (
              <div key={item.key} className="col-6 col-sm-3">
                <div
                  className="bg-white rounded p-3 text-center"
                  style={{ border: '1px solid #fecaca' }}
                >
                  <div
                    className="fw-bold dashboard-number-cell"
                    style={{
                      fontSize: '1.5rem',
                      color: item.color,
                      lineHeight: 1,
                      marginBottom: '0.25rem',
                      fontVariantNumeric: 'tabular-nums',
                      minWidth: '2ch',
                    }}
                  >
                    {loading ? '…' : item.value}
                  </div>
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                    }}
                  >
                    {item.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="d-grid gap-2 mt-3 pt-2" style={{ borderTop: '1px solid #e5e7eb' }}>
          {(stats?.cacheMetadata?.outdatedCount ?? 0) > 0 && (
            <button
              type="button"
              className="btn btn-sm"
              style={{
                fontSize: '0.8rem',
                background: validationDisabled || isQuotaExceededOverall ? '#9ca3af' : '#fef3c7',
                color: validationDisabled || isQuotaExceededOverall ? 'white' : '#b45309',
                border: '1px solid #fcd34d',
                fontWeight: 600,
                borderRadius: '6px',
              }}
              onClick={() => {
                if (!validationDisabled && !isQuotaExceededOverall) {
                  onQuickAction?.('refresh-outdated');
                }
              }}
              disabled={validationDisabled || isQuotaExceededOverall}
              title={isQuotaExceededOverall ? t('dashboard.quotaExceededUpgrade') : undefined}
            >
              {t('dashboard.refreshOutdated', { count: stats.cacheMetadata.outdatedCount })}
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm fw-bold"
            style={{
              fontSize: '0.8rem',
              background: validationDisabled || isQuotaExceededOverall ? '#9ca3af' : '#0d6efd',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
            }}
            onClick={() => {
              if (!validationDisabled && !isQuotaExceededOverall) {
                onQuickAction?.('validate-all');
              }
            }}
            disabled={validationDisabled || isQuotaExceededOverall}
            title={isQuotaExceededOverall ? t('dashboard.quotaExceededUpgrade') : undefined}
          >
            {stats?.cacheMetadata?.isCached
              ? t('dashboard.validateAllAgain')
              : t('dashboard.runFirstValidation')}
          </button>
        </div>
      </div>
    </div>
  );
}
